/**
 * MeasurementEquipment Service
 * API integration for measurement equipment CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de equipamentos de medição
 */
import apiClient from './apiClient';
import {
  MeasurementEquipment,
  MeasurementEquipmentFilters,
  MeasurementEquipmentFormData,
  MeasurementEquipmentApiPayload,
  MeasurementEquipmentListResponse,
  MeasurementEquipmentStatus,
} from '../types/measurementEquipment';

// Re-export types for convenience
export * from '../types/measurementEquipment';

export interface MeasurementEquipmentListParams extends MeasurementEquipmentFilters {
  page?: number;
  limit?: number;
}

class MeasurementEquipmentService {
  private baseUrl = '/measurement-equipments';

  /**
   * Convert form data to API payload
   */
  private formDataToApiPayload(formData: MeasurementEquipmentFormData): MeasurementEquipmentApiPayload {
    return {
      companyId: formData.isGlobal ? undefined : formData.companyId,
      isGlobal: formData.isGlobal,
      name: formData.name,
      serialNumber: formData.serialNumber ?? undefined,
      manufacturer: formData.manufacturer,
      model: formData.model,
      occupationalRiskIds: formData.occupationalRiskIds,
      eSocialCode: formData.eSocialCode ?? undefined,
      calibrationDate: formData.calibrationDate,
      validityDate: formData.validityDate,
      methodology: formData.methodology,
      detectsHarmfulAgents: formData.detectsHarmfulAgents,
      status: formData.status,
    };
  }

  /**
   * Convert API response to form data
   */
  apiResponseToFormData(apiData: MeasurementEquipment): MeasurementEquipmentFormData {
    return {
      companyId: apiData.companyId,
      isGlobal: apiData.isGlobal,
      name: apiData.name,
      serialNumber: apiData.serialNumber ?? '',
      manufacturer: apiData.manufacturer,
      model: apiData.model,
      occupationalRiskIds: apiData.occupationalRisks.map(r => r.id),
      eSocialCode: apiData.eSocialCode ?? '',
      calibrationDate: apiData.calibrationDate,
      validityDate: apiData.validityDate,
      methodology: apiData.methodology,
      detectsHarmfulAgents: apiData.detectsHarmfulAgents,
      status: apiData.status,
    };
  }

  /**
   * Get paginated list of measurement equipment
   */
  async getMeasurementEquipments(params: MeasurementEquipmentListParams = {}): Promise<MeasurementEquipmentListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters.isGlobal !== undefined) queryParams.append('isGlobal', String(filters.isGlobal));
    if (filters.occupationalRiskIds && filters.occupationalRiskIds.length > 0) {
      queryParams.append('occupationalRiskIds', filters.occupationalRiskIds.join(','));
    }
    if (filters.validityStatus) queryParams.append('validityStatus', filters.validityStatus);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.includeGlobal !== undefined) queryParams.append('includeGlobal', String(filters.includeGlobal));
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // Backend returns: { data: { equipments, total, page, limit, totalPages } }
    const backendData = response.data.data || response.data;

    return {
      equipments: backendData.equipments ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  /**
   * Get measurement equipment by ID
   */
  async getMeasurementEquipmentById(id: string | number): Promise<MeasurementEquipment> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Get global equipment
   */
  async getGlobalEquipments(): Promise<MeasurementEquipment[]> {
    const response = await apiClient.get(`${this.baseUrl}/global`);
    return response.data.data || response.data;
  }

  /**
   * Get equipment by company (including global)
   */
  async getByCompany(companyId: number, includeGlobal: boolean = true): Promise<MeasurementEquipment[]> {
    const queryParams = new URLSearchParams();
    if (!includeGlobal) queryParams.append('includeGlobal', 'false');

    const query = queryParams.toString();
    const url = `${this.baseUrl}/company/${companyId}${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.data || response.data;
  }

  /**
   * Get equipment expiring within N days
   */
  async getExpiringEquipments(days: number = 30, companyId?: number): Promise<MeasurementEquipment[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('days', days.toString());
    if (companyId) queryParams.append('companyId', companyId.toString());

    const query = queryParams.toString();
    const url = `${this.baseUrl}/expiring${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.data || response.data;
  }

  /**
   * Get expired equipment
   */
  async getExpiredEquipments(companyId?: number): Promise<MeasurementEquipment[]> {
    const queryParams = new URLSearchParams();
    if (companyId) queryParams.append('companyId', companyId.toString());

    const query = queryParams.toString();
    const url = `${this.baseUrl}/expired${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.data || response.data;
  }

  /**
   * Create new measurement equipment
   */
  async createMeasurementEquipment(data: MeasurementEquipmentFormData): Promise<MeasurementEquipment> {
    const payload = this.formDataToApiPayload(data);
    const response = await apiClient.post(this.baseUrl, payload);
    return response.data.data || response.data;
  }

  /**
   * Update measurement equipment by ID
   */
  async updateMeasurementEquipment(id: string | number, data: Partial<MeasurementEquipmentFormData>): Promise<MeasurementEquipment> {
    const payload = this.formDataToApiPayload(data as MeasurementEquipmentFormData);
    const response = await apiClient.put(`${this.baseUrl}/${id}`, payload);
    return response.data.data || response.data;
  }

  /**
   * Delete measurement equipment by ID (soft delete)
   */
  async deleteMeasurementEquipment(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Activate measurement equipment
   */
  async activateMeasurementEquipment(id: string | number): Promise<MeasurementEquipment> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/activate`);
    return response.data.data || response.data;
  }

  /**
   * Deactivate measurement equipment
   */
  async deactivateMeasurementEquipment(id: string | number): Promise<MeasurementEquipment> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/deactivate`);
    return response.data.data || response.data;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }

  /**
   * Get default form values
   */
  getDefaultFormValues(): MeasurementEquipmentFormData {
    const today = new Date().toISOString().split('T')[0];
    const nextYear = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    return {
      isGlobal: false,
      name: '',
      serialNumber: '',
      manufacturer: '',
      model: '',
      occupationalRiskIds: [],
      calibrationDate: today,
      validityDate: nextYear,
      methodology: '',
      detectsHarmfulAgents: true,
      status: MeasurementEquipmentStatus.ACTIVE,
    };
  }
}

// Export singleton instance
export const measurementEquipmentService = new MeasurementEquipmentService();
export default measurementEquipmentService;
